import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    buildTimeValue,
    formatDisplayTime,
    formatHourLabel,
    formatMinuteLabel,
    generateHourOptions,
    generateMinuteOptions,
    parseTimeParts,
} from './ecxTimeUtils';

function EcxTimePopup({
    value,
    onSelect,
    onClose,
    anchorRef,
    intervalMinutes = 5,
}) {
    const popupRef = useRef(null);
    const listRef = useRef(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [step, setStep] = useState('hour');
    const initialParts = parseTimeParts(value, intervalMinutes);
    const [selectedHour, setSelectedHour] = useState(initialParts.hour);
    const [selectedMinute, setSelectedMinute] = useState(initialParts.minute);

    const hourOptions = generateHourOptions();
    const minuteOptions = generateMinuteOptions(intervalMinutes);
    const previewValue = buildTimeValue(selectedHour, selectedMinute);

    useEffect(() => {
        const nextParts = parseTimeParts(value, intervalMinutes);
        setSelectedHour(nextParts.hour);
        setSelectedMinute(nextParts.minute);
        setStep('hour');
    }, [value, intervalMinutes]);

    const updatePosition = () => {
        const anchor = anchorRef?.current;
        const popup = popupRef.current;

        if (!anchor || !popup) {
            return;
        }

        const anchorRect = anchor.getBoundingClientRect();
        const popupRect = popup.getBoundingClientRect();
        const viewportPadding = 12;

        let top = anchorRect.bottom + 6;
        let left = anchorRect.left;

        if (left + popupRect.width > window.innerWidth - viewportPadding) {
            left = window.innerWidth - popupRect.width - viewportPadding;
        }

        if (left < viewportPadding) {
            left = viewportPadding;
        }

        if (top + popupRect.height > window.innerHeight - viewportPadding) {
            top = anchorRect.top - popupRect.height - 6;
        }

        if (top < viewportPadding) {
            top = viewportPadding;
        }

        setPosition({ top, left });
    };

    useLayoutEffect(() => {
        updatePosition();
    }, [anchorRef, step]);

    useEffect(() => {
        if (!anchorRef?.current) {
            return undefined;
        }

        const handleReposition = () => updatePosition();
        window.addEventListener('resize', handleReposition);
        window.addEventListener('scroll', handleReposition, true);

        return () => {
            window.removeEventListener('resize', handleReposition);
            window.removeEventListener('scroll', handleReposition, true);
        };
    }, [anchorRef]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose?.();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    useEffect(() => {
        const selectedButton = listRef.current?.querySelector('.ecx-time-popup__slot.is-selected');
        selectedButton?.scrollIntoView({ block: 'nearest' });
    }, [step, selectedHour, selectedMinute]);

    const handleHourSelect = (hour) => {
        setSelectedHour(hour);
        setStep('minute');
    };

    const handleMinuteSelect = (minute) => {
        setSelectedMinute(minute);
        onSelect?.(buildTimeValue(selectedHour, minute));
        onClose?.();
    };

    return (
        <div
            ref={popupRef}
            className={`ecx-time-popup ecx-page ecx-time-popup--step-${step}`}
            style={{ top: position.top, left: position.left }}
            role="dialog"
            aria-modal="true"
            aria-label="Choose time"
        >
            <div className="ecx-time-popup__header">
                <div className="ecx-time-popup__title">
                    <i className="bi bi-clock"></i>
                    <span>{step === 'hour' ? 'Select hour' : 'Select minute'}</span>
                    {step === 'minute' && (
                        <span className="ecx-time-popup__preview">
                            {formatHourLabel(selectedHour)}
                        </span>
                    )}
                    {step === 'minute' && previewValue && (
                        <span className="ecx-time-popup__preview ecx-time-popup__preview--final">
                            {formatDisplayTime(previewValue)}
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__close-btn flex-shrink-0"
                    onClick={onClose}
                    aria-label="Close time picker"
                >
                    <i className="bi bi-x-lg"></i>
                </button>
            </div>

            <div className="ecx-time-popup__body">
                {step === 'hour' ? (
                    <div className="ecx-time-popup__step">
                        <div className="ecx-time-popup__column-label">Choose hour</div>
                        <div ref={listRef} className="ecx-time-popup__column-list ecx-time-popup__column-list--grid ecx-time-popup__column-list--hours">
                            {hourOptions.map((hour) => (
                                <button
                                    key={hour}
                                    type="button"
                                    className={`ecx-time-popup__slot ${selectedHour === hour ? 'is-selected' : ''}`}
                                    onClick={() => handleHourSelect(hour)}
                                >
                                    {formatHourLabel(hour)}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="ecx-time-popup__step">
                        <div className="ecx-time-popup__column-label">
                            Choose minutes for {formatHourLabel(selectedHour)}
                        </div>
                        <div ref={listRef} className="ecx-time-popup__column-list ecx-time-popup__column-list--grid">
                            {minuteOptions.map((minute) => (
                                <button
                                    key={minute}
                                    type="button"
                                    className={`ecx-time-popup__slot ${selectedMinute === minute ? 'is-selected' : ''}`}
                                    onClick={() => handleMinuteSelect(minute)}
                                >
                                    :{formatMinuteLabel(minute)}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="ecx-time-popup__footer">
                {step === 'minute' && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary rounded-pill"
                        onClick={() => setStep('hour')}
                    >
                        <i className="bi bi-arrow-left me-1"></i>
                        Back
                    </button>
                )}
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__footer-close-btn ms-auto"
                    onClick={onClose}
                >
                    <i className="bi bi-x-circle me-1"></i>
                    Cancel
                </button>
            </div>
        </div>
    );
}

export default EcxTimePopup;
